/**
 * PBPIG-1 — cart-add guard.
 *
 * Every storefront cart-add ends as a POST to `/cart/add` or `/cart/add.js`: the native product
 * form, Buy Now, quick-add, the quick-add modal, product hotspots and third-party widgets all
 * converge there. This module wraps that single point (`fetch`, `XMLHttpRequest.send`, the `submit`
 * event and `HTMLFormElement.prototype.submit`) instead of gating each call site.
 *
 * Per proposed line:
 *   - variant not in the page registry           -> pass through (fail open)
 *   - registered product with no required fields -> pass through
 *   - required personalization present           -> pass through
 *   - required personalization missing           -> cancel the add, open the Product Builder for
 *                                                   that product, seeded with what the caller sent
 *
 * The registry is emitted by `snippets/pb-cart-guard-register.liquid`, which reuses
 * `pb-product-eligible` semantics and reads required flags from the existing
 * personalization_set / personalization_field metaobjects. No new metafield, no product allowlist,
 * no hardcoded field names.
 *
 * Hard rules: a customer is never refused — every rejection is a redirect into the builder — and
 * anything the guard cannot resolve passes through untouched.
 */

/** Header the Product Builder's own cart-adds carry so the guard leaves them alone. */
export const GUARD_BYPASS_HEADER = 'X-PB-Cart-Guard';
export const GUARD_BYPASS_VALUE = 'builder';

/**
 * Where an intercepted shopper ended up. `bootBuilderOpen` resolves on several paths that never
 * present the modal, so "the promise resolved" is not proof the builder opened — the outcome is
 * asserted, not assumed, and anything short of an open builder lands on the product page.
 */
export const GUARD_OUTCOMES = {
  BUILDER_OPENED: 'builder-opened',
  PRODUCT_PAGE: 'product-page',
};

const NUMBERED_SUFFIX = /\s+\d+$/;

/* ------------------------------------------------------------------ registry */

/** @typedef {{productId: string, handle: string, required: string[], optionNames: string[]}} GuardProduct */

export function createGuardRegistry() {
  return { byVariant: new Map(), byProduct: new Map() };
}

function cleanLabels(list) {
  if (!Array.isArray(list)) return [];
  const out = [];
  for (const raw of list) {
    const label = String(raw == null ? '' : raw).trim();
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}

/**
 * Add one product to the registry. Idempotent — the same product is registered by every surface
 * that can add it, and later registrations only widen the variant index.
 *
 * @param {ReturnType<createGuardRegistry>} registry
 * @param {{id?: string|number, handle?: string, required?: string[], options?: string[], variants?: Array<number|string|[number|string, string]>}} entry
 */
export function registerGuardProduct(registry, entry) {
  if (!registry || !entry || typeof entry !== 'object') return registry;
  const productId = String(entry.id == null ? '' : entry.id).trim();
  const handle = String(entry.handle || '').trim();
  if (!productId || !handle) return registry;

  let product = registry.byProduct.get(productId);
  if (!product) {
    product = { productId, handle, required: [], optionNames: [], variantTitles: new Map() };
    registry.byProduct.set(productId, product);
  }
  product.handle = handle;
  product.required = cleanLabels(entry.required);
  product.optionNames = cleanLabels(entry.options).filter((name) => name.toLowerCase() !== 'title');

  const variants = Array.isArray(entry.variants) ? entry.variants : [];
  for (const variant of variants) {
    const id = Array.isArray(variant) ? variant[0] : variant;
    const title = Array.isArray(variant) ? variant[1] : '';
    const key = String(id == null ? '' : id).trim();
    if (!key) continue;
    registry.byVariant.set(key, product);
    product.variantTitles.set(key, String(title || ''));
  }
  return registry;
}

/**
 * @param {ReturnType<createGuardRegistry>} registry
 * @param {string|number} variantId
 * @returns {GuardProduct|null}
 */
export function lookupVariant(registry, variantId) {
  const key = String(variantId == null ? '' : variantId).trim();
  if (!registry || !key) return null;
  return registry.byVariant.get(key) || null;
}

/**
 * Read every `<script type="application/json" data-pb-cart-guard>` block currently in the document.
 * Called when a cart-add is attempted, so markup injected later (quick-add modal, section renders)
 * is picked up without an observer.
 */
export function readGuardRegistryFromDocument(doc, registry) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return registry;
  const nodes = doc.querySelectorAll('script[type="application/json"][data-pb-cart-guard]');
  for (const node of nodes) {
    try {
      registerGuardProduct(registry, JSON.parse(node.textContent || '{}'));
    } catch (e) {
      /* a malformed block must never break an add */
    }
  }
  return registry;
}

/* ------------------------------------------------------------------ payload parsing */

/** True for `/cart/add` and `/cart/add.js`, with or without a locale or market prefix. */
export function isCartAddUrl(input) {
  if (input == null) return false;
  let pathname = '';
  try {
    pathname = new URL(String(input), 'https://pb-cart-guard.invalid').pathname;
  } catch (e) {
    return false;
  }
  return /\/cart\/add(\.js)?$/.test(pathname);
}

/** `items[0][properties][Note Card]` -> `['items', '0', 'properties', 'Note Card']` */
function parseFieldPath(key) {
  const name = String(key == null ? '' : key);
  const head = name.match(/^[^[]*/);
  const root = head ? head[0] : '';
  if (!root) return [];
  const parts = [root];
  const bracket = /\[([^\]]*)\]/g;
  let match;
  while ((match = bracket.exec(name.slice(root.length))) !== null) parts.push(match[1]);
  return parts;
}

function lineAt(lines, index) {
  while (lines.length <= index) lines.push({ id: '', quantity: 1, properties: {} });
  return lines[index];
}

function applyFieldEntry(lines, key, value) {
  const parts = parseFieldPath(key);
  if (!parts.length) return;
  if (parts[0] === 'items' && /^\d+$/.test(parts[1] || '')) {
    const line = lineAt(lines, Number(parts[1]));
    if (parts[2] === 'id') line.id = String(value);
    else if (parts[2] === 'quantity') line.quantity = Number(value) || 1;
    else if (parts[2] === 'properties' && parts[3]) line.properties[parts[3]] = value;
    return;
  }
  const line = lineAt(lines, 0);
  if (parts[0] === 'id') line.id = String(value);
  else if (parts[0] === 'quantity') line.quantity = Number(value) || 1;
  else if (parts[0] === 'properties' && parts[1]) line.properties[parts[1]] = value;
}

function linesFromObject(payload) {
  const lines = [];
  const items = Array.isArray(payload?.items) ? payload.items : [payload];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    lines.push({
      id: String(item.id == null ? '' : item.id),
      quantity: Number(item.quantity) || 1,
      properties: item.properties && typeof item.properties === 'object' ? { ...item.properties } : {},
    });
  }
  return lines;
}

/**
 * Normalise any `/cart/add` body into proposed lines. Handles JSON (single line or `items[]`),
 * FormData, URLSearchParams and url-encoded strings — the four shapes the storefront and
 * third-party apps actually post.
 *
 * @returns {Array<{id: string, quantity: number, properties: Record<string, any>}>}
 */
export function parseCartAddBody(body) {
  if (body == null) return [];

  if (typeof body === 'string') {
    const trimmed = body.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return linesFromObject(JSON.parse(trimmed));
      } catch (e) {
        return [];
      }
    }
    return parseCartAddBody(new URLSearchParams(trimmed));
  }

  if (typeof body.forEach === 'function' && typeof body.append === 'function') {
    // FormData or URLSearchParams
    const lines = [];
    body.forEach((value, key) => {
      applyFieldEntry(lines, key, typeof value === 'string' ? value : value?.name || '');
    });
    return lines.filter((line) => line.id !== '');
  }

  if (typeof body === 'object') return linesFromObject(body);
  return [];
}

/** Proposed lines for a live `<form action="/cart/add">`. */
export function parseCartAddForm(form, FormDataCtor) {
  const Ctor = FormDataCtor || (typeof FormData !== 'undefined' ? FormData : null);
  if (!form || !Ctor) return [];
  try {
    return parseCartAddBody(new Ctor(form));
  } catch (e) {
    return [];
  }
}

/* ------------------------------------------------------------------ completeness */

export function isFilledValue(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.some(isFilledValue);
  return String(value).trim() !== '';
}

/**
 * Value a line carries for a required field label. Matching is case-insensitive and tolerates the
 * builder's numbered multi-value keys (`Name 1`, `Name 2`).
 */
export function readPropertyValue(properties, label) {
  const want = String(label == null ? '' : label).trim().toLowerCase();
  if (!want || !properties || typeof properties !== 'object') return undefined;
  for (const key of Object.keys(properties)) {
    const normalized = String(key).trim().toLowerCase();
    if (normalized === want) return properties[key];
    if (normalized.replace(NUMBERED_SUFFIX, '') === want && isFilledValue(properties[key])) {
      return properties[key];
    }
  }
  return undefined;
}

/** Required labels this line does not answer. */
export function missingRequiredLabels(product, properties) {
  if (!product || !Array.isArray(product.required)) return [];
  return product.required.filter((label) => !isFilledValue(readPropertyValue(properties, label)));
}

function buildSeed(product, line) {
  const selections = {};
  for (const [key, value] of Object.entries(line.properties || {})) {
    if (String(key).startsWith('_')) continue; // hidden/system properties are not builder answers
    if (!isFilledValue(value)) continue;
    selections[key] = value;
  }
  const variantTitle = product.variantTitles?.get(String(line.id)) || '';
  const values = variantTitle ? variantTitle.split(' / ') : [];
  product.optionNames.forEach((name, index) => {
    const value = values[index];
    if (isFilledValue(value) && !isFilledValue(selections[name])) selections[name] = value.trim();
  });
  return {
    variantId: line.id,
    quantity: line.quantity,
    properties: { ...(line.properties || {}) },
    selections,
  };
}

/**
 * Decide what to do with a proposed cart-add.
 *
 * @returns {{action: 'pass'} | {action: 'intercept', handle: string, productId: string, missing: string[], seed: object}}
 */
export function planCartAdd(lines, registry) {
  if (!Array.isArray(lines) || !lines.length) return { action: 'pass' };
  for (const line of lines) {
    const product = lookupVariant(registry, line.id);
    if (!product) continue; // unknown variant — fail open
    if (!product.required.length) continue; // eligible, but nothing the config requires
    const missing = missingRequiredLabels(product, line.properties);
    if (!missing.length) continue;
    return {
      action: 'intercept',
      handle: product.handle,
      productId: product.productId,
      missing,
      seed: buildSeed(product, line),
    };
  }
  return { action: 'pass' };
}

/* ------------------------------------------------------------------ seeding */

/**
 * Pre-fill the builder with whatever the intercepted caller supplied. Never overwrites an answer
 * the builder already holds (a resumed draft wins).
 */
export function applyGuardSeed(pb, seed) {
  if (!seed) return false;
  const state = pb?.state || pb?.builderState || pb;
  const ctx = state?.main || state;
  if (!ctx || typeof ctx !== 'object') return false;
  if (!ctx.selections || typeof ctx.selections !== 'object') ctx.selections = {};
  if (!ctx.touched || typeof ctx.touched !== 'object') ctx.touched = {};
  let applied = false;
  for (const [key, value] of Object.entries(seed.selections || {})) {
    if (isFilledValue(ctx.selections[key])) continue;
    ctx.selections[key] = Array.isArray(value) ? value.join(', ') : String(value);
    ctx.touched[key] = true;
    applied = true;
  }
  try {
    state.pbGuardSeed = seed;
  } catch (e) {
    /* frozen state is not a reason to fail the open */
  }
  return applied;
}

/**
 * True when the builder now on screen belongs to the product we asked for. Reads the same
 * `BUILDER_DATA.mainProduct` the builder itself renders from, so it catches any mount that left
 * the wrong product in charge.
 */
export function openedBuilderMatches(scope, productId) {
  const mounted = scope?.BUILDER_DATA?.mainProduct?.id;
  if (mounted == null || mounted === '') return true; // nothing to compare — do not block the open
  return String(mounted) === String(productId);
}

/**
 * True when this page renders a Product Builder of its own, inside a section — a PDP.
 *
 * One builder per page. `sections/product-builder-section.liquid` owns nine fixed element ids
 * (`product-builder-root`, `builder-modal`, `main-flow-container`, `sub-flow-container` and the
 * rest), so a second builder mounted over it duplicates every one of them and `getElementById`
 * keeps answering with the page's. That is not a thing to patch id by id: it is a reason not to
 * mount a second builder here at all.
 */
export function pageHostsOwnBuilder(doc) {
  if (!doc || typeof doc.querySelector !== 'function') return false;
  return !!doc.querySelector('[id^="shopify-section-"] #product-builder-root');
}

/**
 * A cross-product interception on a PDP belongs on the other product's own page, where its
 * builder is the page's builder and no ids collide. Everywhere else — cart, cart drawer,
 * collection, search — nothing owns those ids, so the builder opens in place.
 */
export function mustOpenOnProductPage(scope, doc, productId) {
  return pageHostsOwnBuilder(doc) && !openedBuilderMatches(scope, productId);
}

/* ------------------------------------------------------------------ install */

function headerBypassed(input, init) {
  const wanted = GUARD_BYPASS_HEADER.toLowerCase();
  const read = (headers) => {
    if (!headers) return false;
    if (typeof headers.get === 'function') return !!headers.get(GUARD_BYPASS_HEADER);
    if (Array.isArray(headers)) return headers.some(([key]) => String(key).toLowerCase() === wanted);
    if (typeof headers === 'object') {
      return Object.keys(headers).some((key) => String(key).toLowerCase() === wanted);
    }
    return false;
  };
  return read(init?.headers) || read(input?.headers);
}

function interceptedPayload() {
  return JSON.stringify({ items: [], status: null, __pbCartGuard: 'opened-builder' });
}

function fakeXhrCompletion(xhr, payload) {
  const define = (key, value) => {
    try {
      Object.defineProperty(xhr, key, { configurable: true, get: () => value });
    } catch (e) {
      /* some hosts lock these down; the abandoned add is still the important half */
    }
  };
  define('readyState', 4);
  define('status', 200);
  define('statusText', 'OK');
  define('responseText', payload);
  define('responseURL', xhr.__pbGuardUrl || '');
  let parsed = payload;
  if (xhr.responseType === 'json') {
    try {
      parsed = JSON.parse(payload);
    } catch (e) {
      parsed = null;
    }
  }
  define('response', parsed);
  setTimeout(() => {
    for (const type of ['readystatechange', 'load', 'loadend']) {
      try {
        xhr.dispatchEvent(new Event(type));
      } catch (e) {
        /* ignore */
      }
    }
  }, 0);
}

/**
 * Wrap every cart-add entry point on `scope`.
 *
 * @param {{scope?: object, doc?: object, openBuilder?: (plan: object) => Promise<any>}} [options]
 */
export function installCartAddGuard(options = {}) {
  const scope = options.scope || (typeof window !== 'undefined' ? window : null);
  if (!scope) return null;
  if (scope.PBCartGuard?.installed) return scope.PBCartGuard;
  const doc = options.doc || scope.document;

  const guard = {
    installed: true,
    registry: createGuardRegistry(),
    pendingSeed: null,
    lastPlan: null,
    lastOutcome: null,
    lastError: null,
    opening: false,
    register(entry) {
      return registerGuardProduct(guard.registry, entry);
    },
    refresh() {
      return readGuardRegistryFromDocument(doc, guard.registry);
    },
    plan(lines) {
      guard.refresh();
      return planCartAdd(lines, guard.registry);
    },
  };

  async function defaultOpenBuilder(plan) {
    const boot = await import('@theme/pb-builder-boot');
    await boot.bootBuilderOpen({
      entry: 'card',
      handle: plan.handle,
      productId: plan.productId,
      // Mirror assets/pb-card-open.js, the proven entry for "open the builder for an arbitrary
      // product". Its loading shell is off on purpose: closing the cart drawer out from under the
      // shell reads as a dismissal, and bootBuilderOpen then returns without presenting anything.
      loadingShell: false,
      suspendCartDrawer: true,
      beforePresent: (pb) => applyGuardSeed(pb, plan.seed),
    });
    // bootBuilderOpen returns early — without throwing — when the draft gate aborts, when the
    // loading shell is dismissed, or when the modal ends up behind another top-layer dialog such
    // as the open cart drawer. Assert the postcondition instead of trusting the resolve.
    if (!boot.isBuilderModalMeasurable()) {
      boot.recoverBuilderScrollLock?.();
      boot.recoverStaleBuilderChrome?.();
      throw new Error('the builder did not present');
    }
    // An open builder is not enough — it has to be the builder for the product we intercepted.
    // Handing a shopper the wrong product's fields would manufacture exactly the defect this
    // guard exists to prevent, so a mismatch is a failed open, not a success.
    if (!openedBuilderMatches(scope, plan.productId)) {
      boot.recoverBuilderScrollLock?.();
      boot.recoverStaleBuilderChrome?.();
      throw new Error('the builder opened on a different product');
    }
  }

  const openBuilder = options.openBuilder || defaultOpenBuilder;

  /** The product page for an intercepted line, on the variant the shopper had chosen. */
  function productPageUrl(plan) {
    const root = scope.Shopify?.routes?.root || '/';
    const variant = plan.seed?.variantId;
    return `${root}products/${plan.handle}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
  }

  /**
   * Never a dead end. The guard's contract is that an intercepted shopper ends up somewhere they
   * can personalize — so if the builder does not open, for any reason, send them to the product
   * page rather than leaving a pressed button that did nothing.
   */
  async function openBuilderOrFallback(plan) {
    try {
      if (mustOpenOnProductPage(scope, doc, plan.productId)) {
        throw new Error('this page already hosts another product builder');
      }
      await openBuilder(plan);
      guard.lastOutcome = GUARD_OUTCOMES.BUILDER_OPENED;
      guard.lastError = null;
      return;
    } catch (err) {
      guard.lastError = err;
    }
    guard.lastOutcome = GUARD_OUTCOMES.PRODUCT_PAGE;
    try {
      scope.location.assign(productPageUrl(plan));
    } catch (e) {
      /* nothing further we can do without refusing the customer */
    }
  }

  function redirectToBuilder(plan) {
    guard.pendingSeed = plan.seed;
    guard.lastPlan = plan;
    if (guard.opening) return;
    guard.opening = true;
    openBuilderOrFallback(plan).then(
      () => {
        guard.opening = false;
      },
      () => {
        guard.opening = false;
      }
    );
  }

  function planFor(lines) {
    try {
      return guard.plan(lines);
    } catch (e) {
      return { action: 'pass' }; // fail open
    }
  }

  /* --- fetch --- */
  if (typeof scope.fetch === 'function') {
    const originalFetch = scope.fetch.bind(scope);
    guard.originalFetch = originalFetch;

    async function guardedCartAdd(input, init) {
      let plan = { action: 'pass' };
      try {
        let body = init?.body;
        if (body === undefined && input && typeof input === 'object' && typeof input.clone === 'function') {
          body = await input.clone().text();
        }
        plan = planFor(parseCartAddBody(body));
      } catch (e) {
        plan = { action: 'pass' };
      }
      if (plan.action !== 'intercept') return originalFetch(input, init);
      redirectToBuilder(plan);
      return new Response(interceptedPayload(), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    }

    scope.fetch = function pbGuardedFetch(input, init) {
      const url = typeof input === 'string' ? input : input?.url ?? input;
      if (!isCartAddUrl(url)) return originalFetch(input, init);
      if (headerBypassed(input, init)) return originalFetch(input, init);
      return guardedCartAdd(input, init);
    };
  }

  /* --- XMLHttpRequest --- */
  if (typeof scope.XMLHttpRequest === 'function') {
    const proto = scope.XMLHttpRequest.prototype;
    const originalOpen = proto.open;
    const originalSend = proto.send;
    const originalSetHeader = proto.setRequestHeader;

    proto.open = function pbGuardedOpen(method, url, ...rest) {
      this.__pbGuardUrl = url;
      this.__pbGuardBypass = false;
      return originalOpen.call(this, method, url, ...rest);
    };

    proto.setRequestHeader = function pbGuardedSetHeader(name, value) {
      if (String(name).toLowerCase() === GUARD_BYPASS_HEADER.toLowerCase()) this.__pbGuardBypass = true;
      return originalSetHeader.call(this, name, value);
    };

    proto.send = function pbGuardedSend(body) {
      try {
        if (isCartAddUrl(this.__pbGuardUrl) && !this.__pbGuardBypass) {
          const plan = planFor(parseCartAddBody(body));
          if (plan.action === 'intercept') {
            redirectToBuilder(plan);
            fakeXhrCompletion(this, interceptedPayload());
            return undefined;
          }
        }
      } catch (e) {
        /* fail open */
      }
      return originalSend.call(this, body);
    };
  }

  /* --- form submit (event and programmatic) --- */
  const isCartAddForm = (form) =>
    !!form && typeof form.getAttribute === 'function' && isCartAddUrl(form.getAttribute('action'));

  if (doc && typeof doc.addEventListener === 'function') {
    doc.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!isCartAddForm(form)) return;
        const plan = planFor(parseCartAddForm(form, scope.FormData));
        if (plan.action !== 'intercept') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        redirectToBuilder(plan);
      },
      true
    );
  }

  if (typeof scope.HTMLFormElement === 'function') {
    const originalSubmit = scope.HTMLFormElement.prototype.submit;
    scope.HTMLFormElement.prototype.submit = function pbGuardedFormSubmit(...args) {
      try {
        if (isCartAddForm(this)) {
          const plan = planFor(parseCartAddForm(this, scope.FormData));
          if (plan.action === 'intercept') {
            redirectToBuilder(plan);
            return undefined;
          }
        }
      } catch (e) {
        /* fail open */
      }
      return originalSubmit.apply(this, args);
    };
  }

  scope.PBCartGuard = guard;
  return guard;
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  installCartAddGuard();
}
